import { AlertCircle } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/cn.js';

export interface FormFieldProps {
  label: React.ReactNode;
  /** Helper text shown below the control. */
  hint?: React.ReactNode;
  /** Validation message. Sets aria-invalid + aria-describedby on the child control. */
  error?: React.ReactNode;
  required?: boolean;
  className?: string;
  /** A single form control element; id/aria props are injected. */
  children: React.ReactElement<Record<string, unknown>>;
  id?: string;
}

export function FormField({ label, hint, error, required, className, children, id }: FormFieldProps) {
  const autoId = React.useId();
  const controlId = id ?? (children.props['id'] as string | undefined) ?? autoId;
  const hintId = hint ? `${controlId}-hint` : undefined;
  const errorId = error ? `${controlId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  const control = React.cloneElement(children, {
    id: controlId,
    'aria-describedby': describedBy,
    'aria-invalid': error ? true : undefined,
    'aria-required': required || undefined,
  });
  return (
    <div className={cn('grid gap-1.5', className)}>
      <label htmlFor={controlId} className="text-body font-medium text-text-primary">
        {label}
        {required ? (
          <span className="ml-0.5 text-danger" aria-hidden>
            *
          </span>
        ) : null}
      </label>
      {control}
      {hint ? (
        <p id={hintId} className="text-helper text-text-secondary">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} role="alert" className="flex items-center gap-1.5 text-helper font-medium text-danger">
          <AlertCircle aria-hidden className="size-4 shrink-0" />
          {error}
        </p>
      ) : null}
    </div>
  );
}
